import { useContext, useState } from 'react';
import { Role } from 'types';
import { CheckIcon, ChevronsUpDownIcon, Plus, WandSparkles } from 'lucide-react';
import { Button } from './shadcn/button';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from './shadcn/command';
import { Popover, PopoverContent, PopoverTrigger } from './shadcn/popover';
import { useChatbotList } from 'hooks/useChatbotList';
import { AuthContext } from 'contexts';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './shadcn/dialog';

interface Props {
  width: string;
}

export function SelectBot({ width }: Props) {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const { isSuccess, data, isError, isFetching } = useChatbotList();
  const { role } = useContext(AuthContext);

  const navigate = useNavigate();

  const { action, id } = useParams();

  const items = data?.map((e) => ({ value: e.id, label: e.name })) || [];

  if (isFetching) {
    return (
      <Button variant="outline" className={`${width} justify-between`} disabled>
        {t('common.loading')}
      </Button>
    );
  }

  if (isError) {
    return <p>{t('common.error')}</p>;
  }

  if (isSuccess && (!items || !items.length)) {
    return (
      <Button variant="outline" className={`${width} justify-between`} disabled>
        {t('dashboard.selectBot.noChatbotsAvailable')}
      </Button>
    );
  }

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={`${width} justify-between `}
          >
            <p className="truncate">
              {id && items.length && items.find((e) => e.value === id)?.label}
            </p>
            <ChevronsUpDownIcon className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[250px] p-0">
          <Command className={role === Role.ADMIN ? 'pb-1' : ''}>
            {role === Role.ADMIN && <CommandInput placeholder={t('common.search')} />}
            <CommandList>
              <CommandGroup>
                {items.map((e, index) => (
                  <CommandItem
                    className="flex justify-between px-3 "
                    key={e.value}
                    value={e.label}
                    onSelect={() => {
                      setOpen(false);
                      if (e.value !== id) {
                        const actionPath = action === 'create' ? 'edit' : action;
                        navigate(`/${e.value}/${actionPath}`);
                      }
                    }}
                  >
                    <p className="truncate">{e.label}</p>
                    <CheckIcon
                      size={16}
                      className={
                        id === e.value || (!id && index === 0) ? 'opacity-100' : 'opacity-0'
                      }
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          {role == Role.ADMIN && (
            <div className="border-t py-1">
              <div className="sticky bottom-0 p-1">
                <Button
                  variant="ghost"
                  className="w-full h-full flex flex-col gap-2 py-1 px-3"
                  onClick={() => {
                    setIsDialogOpen(true);
                  }}
                >
                  <div className="w-full flex justify-between">
                    <p className="text-sm font-normal text-muted-foreground'">
                      {t('dashboard.selectBot.createNewBot')}
                    </p>
                    <Plus size={16} />
                  </div>
                  <div className="w-full flex justify-start items-center gap-2">
                    <div className="bg-green-50 rounded-full flex items-center justify-center">
                      <WandSparkles size={10} className="text-green-500" />
                    </div>
                    <p className="text-muted-foreground text-xs font-normal">
                      {t('dashboard.selectBot.requiresSubscription')}
                    </p>
                  </div>
                </Button>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent showX={false} className="p-3 sm:rounded-2xl">
          <div className="w-full h-fit">
            <img
              src="/new-bot.png"
              alt="Confirmation"
              className="w-full brightness-150 rounded-md aspect-auto ring-1 ring-inset ring-black/10 dark:ring-white/10"
            />
          </div>
          <DialogHeader>
            <DialogTitle className="text-lg leading-none font-medium">
              {t('dashboard.selectBot.dialog.title')}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {t('dashboard.selectBot.dialog.description')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/create`)}>
              {t('dashboard.selectBot.dialog.continue')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
